import { useContext, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import moment from 'moment'
import { FLIGHTS_MAX_LEAD_TIME_DAYS } from 'constants/flight'
import AccommodationPackageTilePricingBlocks from 'components/AccommodationKit/AccommodationPackageTilePricingBlocks'
import AccommodationPackageTilePricingBlock, { ACCOMMODATION_PACKAGE_TITLE } from 'components/AccommodationKit/AccommodationPackageTilePricingBlock'
import { getDisplayTotalPrice } from 'lib/hotels/bedbankUtils'
import BedbankTwinShare from 'components/OfferList/OfferListTiles/Support/BedbankTwinShare'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import useFlightDeals from 'hooks/Flights/useFlightDeals'
import FlightDealLabel from 'components/OfferPage/FlightDeal/FlightDealLabel'
import { useAppSelector } from 'hooks/reduxHooks'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import BedbankMarginText from '../BedbankMargin'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import useBedbankDiscount from 'hooks/useBedbankDiscount'
import { useInView } from 'react-intersection-observer'
import { trackEvent } from 'analytics/analytics'
import { bedbankRateImpressionEventWithContext } from 'analytics/snowplow/events'
import CurrencyContext from 'contexts/currencyContext'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import BodyText from 'LuxKit/Typography/BodyText'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { getIsLuxPlusLPPEnabled } from 'luxPlus/selectors/featureToggle'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import { generateLoyaltyPointsCalculatorBedbankOptions, generateLoyaltyPointsCalculatorBundledFlightOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import GeoContext from 'contexts/geoContext'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import PayLaterPill from 'components/Common/PayLaterPill'
import { offerFlightsEnabled } from 'selectors/offerPage/offerPageSelectors'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import Caption from 'LuxKit/Typography/Caption'
import Group from 'components/utils/Group'
import { EmptyArray, sortBy } from 'lib/array/arrayUtils'

interface Props {
  promotion?: App.BedbankPromotion
  rate: App.BedbankRate
  bundleRate: App.BedbankRate
  rooms: Array<OfferPageRoom>
  offer: App.BedbankOffer | App.BedbankOfferSummary
  flightOrigin?: App.Airport
  flightPrice?: number
  checkIn?: string
  checkOut?: string
  departureTooEarly?: boolean
  flightsEnabled?: boolean
  ratePosition: number
  rateListId?: string
  roomPosition?: number
  roomListId?: string
  luxPlusLPPEnabled: boolean
  isFlightsSelected: boolean
  showPoints: boolean
  discountPosition: 'above' | 'valueRow'
}

function BedbankMultiIndividualOptionTilePricing(props: Props) {
  const {
    rate,
    bundleRate,
    promotion,
    rooms,
    offer,
    checkIn,
    checkOut,
    flightOrigin,
    flightPrice,
    departureTooEarly,
    flightsEnabled,
    ratePosition,
    rateListId,
    roomPosition,
    roomListId,
    luxPlusLPPEnabled,
    isFlightsSelected,
    showPoints,
    discountPosition,
  } = props
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const [deals] = useFlightDeals('all', {
    originAirportCode: flightOrigin?.code,
    destinationAirportCode: offer.flights?.airportCode,
  })
  const hasFlights = !!flightPrice

  const {
    propertyFees = 0,
    taxesAndFees,
  } = rate.totals
  const bundlePropertyFees = bundleRate?.totals.propertyFees || propertyFees
  const bundleTaxesAndFees = bundleRate?.totals.taxesAndFees || taxesAndFees

  const hotelPrice = rate.totals.inclusive
  const hotelMemberPrice = rate.totals.memberInclusive ?? 0
  const bundlePrice = bundleRate?.totals.inclusive || hotelPrice
  const bundleMemberPrice = bundleRate?.totals.memberInclusive || hotelMemberPrice
  const showFlightsDealTag = flightOrigin?.code && offer.flights?.airportCode && flightsEnabled && deals.length > 0

  const activeDeal = useMemo(
    () => (deals.length > 0 ? sortBy(deals, (d: App.FlightDeal) => d.perAdultPrice, 'asc')[0] : undefined),
    [deals],
  )

  const departureTooFarInFuture = useMemo(() => {
    if (checkIn) {
      return moment(checkIn).diff(moment.utc(), 'days') > FLIGHTS_MAX_LEAD_TIME_DAYS
    }
    return false
  }, [checkIn])

  const hasDates = !!checkIn

  const totalPrices = getDisplayTotalPrice({
    hotelPrice: hotelPrice + propertyFees,
    hotelMemberPrice: hotelMemberPrice > 0 ? hotelMemberPrice + propertyFees : 0,
    hotelValue: rate.value,
    bundlePrice: bundlePrice + bundlePropertyFees,
    bundleValue: bundleRate?.value,
    checkIn,
    flightPrice,
    rooms,
    displayPricingAsPerPerson: offer.displayPricingAsPerPerson,
  })
  const saleUnit = offer.displayPricingAsPerPerson ? 'person' : 'total'
  const isPromoRate = rate.id === promotion?.id

  const margin = rate.margin
  const marginAud = rate.marginAud
  const supplier = rate.supplier
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const showMarginInSpoofMode = useAppSelector(state => state.system.showMarginInSpoofMode)
  const displayMargin = Boolean(margin && marginAud && supplier && isSpoofed && showMarginInSpoofMode)
  const discount = useBedbankDiscount(rate.discount, rate.memberDiscount)

  const bundleMargin = bundleRate?.margin
  const bundleMarginAud = bundleRate?.marginAud
  const bundleSupplier = bundleRate?.supplier
  const displayBundleMargin = Boolean(bundleMargin && bundleMarginAud && bundleSupplier && isSpoofed && showMarginInSpoofMode)
  const canShowFlightBundlePrice = flightsEnabled && hasFlights && !departureTooEarly

  const hotelPriceCaptionNoDates = intl.formatMessage(
    {
      id: 'wlaRtk9FEJyY-n8dPS05',
      defaultMessage: '{nights, plural, one {# night} other {# nights}}',
    },
    { nights: rate.nights },
  )
  const hotelPriceCaptionWithDates = intl.formatMessage(
    {
      id: 'YO6yGnUA3WW5diRJBpBB',
      defaultMessage: '{nights, plural, one {# night} other {# nights}} from',
    },
    { nights: rate.nights },
  )
  const bundlePriceCaptionNoDates = intl.formatMessage(
    {
      id: 'rv-RevAtu0T1NO_VVnP_',
      defaultMessage: '{nights, plural, one {# night} other {# nights}}',
    },
    { nights: bundleRate.nights },
  )
  const bundlePriceCaptionWithDates = intl.formatMessage(
    {
      id: 'h9PS61-Cx278MwQgjPWj',
      defaultMessage: '{nights, plural, one {# night} other {# nights}} from',
    },
    { nights: bundleRate.nights },
  )
  const flightBundlePriceCaption = intl.formatMessage(
    {
      id: '_0AUJhNtiyENAEnH7ASr',
      defaultMessage: '{nights, plural, one {# night + flights from} other {# nights + flights from}}',
    },
    { nights: rate.nights },
  )

  const currency = useContext(CurrencyContext)

  const [ref, inView] = useInView({ triggerOnce: true })

  useEffect(() => {
    if (inView && checkIn && checkOut && rateListId && roomPosition && roomListId) {
      trackEvent(bedbankRateImpressionEventWithContext({
        ratePosition,
        rateListId,
        roomPosition,
        roomListId,
        offer,
        rate,
        price: totalPrices.totals.price,
        value: totalPrices.totals.value,
        checkIn,
        checkOut,
        occupancy: rooms,
        currency,
        isFlightBundle: false,
      }))
    }
  }, [inView, checkIn, checkOut, ratePosition, rateListId, roomPosition, roomListId, offer, rate, totalPrices.totals.price, totalPrices.totals.value, rooms, currency])

  useEffect(() => {
    if (inView && checkIn && checkOut && bundleRate && canShowFlightBundlePrice && rateListId && roomPosition && roomListId) {
      trackEvent(bedbankRateImpressionEventWithContext({
        ratePosition,
        rateListId,
        roomPosition,
        roomListId,
        offer,
        rate: bundleRate,
        price: totalPrices.totals.flightBundlePrice,
        value: totalPrices.totals.flightBundleValue,
        checkIn,
        checkOut,
        occupancy: rooms,
        currency,
        isFlightBundle: true,
      }))
    }
  }, [inView, checkIn, checkOut, ratePosition, rateListId, roomPosition, roomListId, offer, bundleRate, totalPrices.totals.flightBundlePrice, totalPrices.totals.flightBundleValue, rooms, currency, canShowFlightBundlePrice])

  const showHotelAndFlightsToggle = flightsEnabled
  const hideHotelPriceBlock = showHotelAndFlightsToggle && isFlightsSelected
  const hideHotelAndFlightsPriceBlock = showHotelAndFlightsToggle && !isFlightsSelected

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    if (!showPoints) { return [] }
    return [
      generateLoyaltyPointsCalculatorBedbankOptions(offer, rate, {
        price: totalPrices.totals.price,
        memberPrice: totalPrices.totals.memberPrice,
        propertyFees,
        currentRegionCode,
      }),
      generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
        flightPrice,
        skip: !canShowFlightBundlePrice || hideHotelAndFlightsPriceBlock,
      }),
    ]
  }, [
    rate,
    totalPrices.totals.price,
    totalPrices.totals.memberPrice,
    propertyFees,
    offer,
    flightPrice,
    canShowFlightBundlePrice,
    hideHotelAndFlightsPriceBlock,
    showPoints,
    currentRegionCode,
  ])

  const canPayLater = !!rate.paymentScheduleInfo?.staticSchedule?.supplierPaymentNumberOfDays && !hideHotelPriceBlock

  return <>
    {canPayLater && <div><PayLaterPill /></div>}

    <AccommodationPackageTilePricingBlocks>
      {!hideHotelPriceBlock && <AccommodationPackageTilePricingBlock
        data-testid="pricing"
        title={(flightsEnabled && !showHotelAndFlightsToggle) ? ACCOMMODATION_PACKAGE_TITLE.HOTEL : undefined}
      >
        <VerticalSpacer gap={12}>
          {discount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE && discountPosition === 'above' && <PriceRowValueDiscount discountPercentage={discount} type="estimate" />}
          <div>
            <PriceRowPriceCaption>
              {hasDates ? hotelPriceCaptionWithDates : hotelPriceCaptionNoDates}
            </PriceRowPriceCaption>
            <LuxPlusPriceGuard
              showMemberPrice={(totalPrices.totals.memberPrice ?? 0) > 0 && luxPlusLPPEnabled}
              fallbackPrice={<PriceRowPrice
                data-testid="bedbank-package-price"
                size="M"
                price={totalPrices.totals.price}
                saleUnit={saleUnit}
              />}
            >
              <LuxPlusPriceStack
                size="M"
                price={totalPrices.totals.price}
                memberPrice={totalPrices.totals.memberPrice}
                saleUnit={saleUnit}
              />
            </LuxPlusPriceGuard>
            <div ref={ref}></div>
            {offer.displayPricingAsPerPerson && <BedbankTwinShare rooms={rooms} />}
            {!isPromoRate && <PriceRowTaxesAndFees />}

            <PriceRowAgentHubCommission
              productType={offer.productType}
              offerLocations={[]}
              offerId={offer.id}
              packageId={rate.id}
              offerTotal={totalPrices.original.price}
              kind="plain"
            />

            {!!discount && <PriceRowValueDiscountWithCaption
              size="M"
              originalValue={totalPrices.totals.value}
              discountPercentage={discount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE && discountPosition === 'valueRow' ? discount : undefined}
              discountType="estimate"
            />}
            {!isPromoRate && <OfferPriceDetailsRow
              trigger="price-row"
              triggerSize="M"
              offerPage
              offer={offer}
              rooms={rooms}
              propertyFees={propertyFees}
              taxesAndFees={taxesAndFees}
              hotelPrice={hotelPrice}
              hotelMemberPrice={hotelMemberPrice}
              showMemberPrice={luxPlusLPPEnabled && hotelMemberPrice > 0}
              duration={rate.nights}
              hasMobilePromotion={rate.mobilePromotion}
            />}
            {displayMargin && <BedbankMarginText
              margin={margin!}
              marginAud={marginAud!}
              supplier={supplier!}
              type="bedbank_exact"
            />}
          </div>
          <LoyaltyPointsEarnWithBreakdownModal
            calculationRequests={pointsEarnCalculationRequests}
            calculationType={!checkIn ? 'estimate' : 'exact'}
          />
        </VerticalSpacer>
      </AccommodationPackageTilePricingBlock>}
      {flightsEnabled && flightOrigin && !hideHotelAndFlightsPriceBlock && <AccommodationPackageTilePricingBlock
        data-testid="flights-pricing"
        title={!showHotelAndFlightsToggle ? ACCOMMODATION_PACKAGE_TITLE.HOTEL_AND_FLIGHTS : undefined}
      >
        <VerticalSpacer gap={12}>
          {!hasFlights && flightOrigin.code === offer.flights?.airportCode && <BodyText
            variant="small"
            colour="light-foreground-alt"
          >
            {intl.formatMessage({
              id: 'QQg7ek3v6jGL81PhIzy5',
              defaultMessage: 'No flights available from {airportName}. Please select another departing airport.',
            }, { airportName: flightOrigin.name })}
          </BodyText>}
          {!hasFlights && departureTooFarInFuture && (
            <BodyText variant="small" colour="light-foreground-alt">
              {intl.formatMessage({
                id: 'nW-s6s_wrzcn0c2oITdD',
                defaultMessage: 'Flights can only be booked up to 11 months in advance.',
              })}
            </BodyText>
          )}
          {flightOrigin.code !== offer.flights?.airportCode && departureTooEarly && <BodyText
            variant="small"
            colour="light-foreground-alt"
          >
            {intl.formatMessage({
              id: 'H-dmnQDd0PrQGtb5myyv',
              defaultMessage: 'Flights cannot be booked online when check-in is within 24 hours.',
            })}
          </BodyText>}
        </VerticalSpacer>

        {!hasFlights && flightOrigin.code !== offer.flights?.airportCode && !departureTooFarInFuture && !departureTooEarly && (
          <VerticalSpacer gap={12}>
            {discount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE && discountPosition === 'above' && <PriceRowValueDiscount discountPercentage={discount} type="estimate" />}
            <div>
              <PriceRowPriceCaption>
                {hasDates ? bundlePriceCaptionWithDates : bundlePriceCaptionNoDates}
              </PriceRowPriceCaption>
              <Group direction="horizontal" verticalAlign="center" gap={4}>
                <LuxPlusPriceGuard
                  showMemberPrice={(bundleMemberPrice ?? 0) > 0 && luxPlusLPPEnabled}
                  fallbackPrice={<PriceRowPrice
                    data-testid="bedbank-package-price"
                    size="M"
                    price={bundlePrice}
                    saleUnit=""
                  />}
                >
                  <LuxPlusPriceStack
                    size="M"
                    price={bundlePrice}
                    memberPrice={bundleMemberPrice}
                    saleUnit=""
                  />
                </LuxPlusPriceGuard>
                <Caption variant="large">{intl.formatMessage({
                  id: 'jm0jmTQ_QnySuWbeRGdD',
                  defaultMessage: '+ flights',
                })}
                </Caption>
              </Group>
              <div ref={ref}></div>
              {offer.displayPricingAsPerPerson && <BedbankTwinShare rooms={rooms} />}

              <PriceRowAgentHubCommission
                productType={offer.productType}
                offerLocations={EmptyArray}
                offerId={offer.id}
                packageId={bundleRate.id}
                offerTotal={bundlePrice}
                kind="plain"
              />

              {!isPromoRate && <OfferPriceDetailsRow
                trigger="price-row"
                triggerSize="M"
                offerPage
                offer={offer}
                rooms={rooms}
                propertyFees={bundlePropertyFees}
                taxesAndFees={bundleTaxesAndFees}
                hotelPrice={bundlePrice}
                hotelMemberPrice={bundleMemberPrice}
                showMemberPrice={luxPlusLPPEnabled && bundleMemberPrice > 0}
                duration={bundleRate.nights}
                hasMobilePromotion={bundleRate.mobilePromotion}
              />}
              {displayMargin && <BedbankMarginText
                margin={bundleMargin!}
                marginAud={bundleMarginAud!}
                supplier={supplier!}
                type="bedbank_exact"
              />}
            </div>
            <LoyaltyPointsEarnWithBreakdownModal
              calculationRequests={pointsEarnCalculationRequests}
              calculationType={!checkIn ? 'estimate' : 'exact'}
            />
          </VerticalSpacer>
        )}

        {canShowFlightBundlePrice && <VerticalSpacer gap={12}>
          {discount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE && discountPosition === 'above' && <PriceRowValueDiscount discountPercentage={discount} type="estimate" />}
          {showFlightsDealTag && activeDeal && <FlightDealLabel activeDeal={activeDeal} showCountdown={false} />}
          <div>
            <PriceRowPriceCaption>
              {showHotelAndFlightsToggle && <LinePlaneDepartureIcon size="S" />} {flightBundlePriceCaption}
            </PriceRowPriceCaption>
            <LuxPlusPriceGuard
              showMemberPrice={(totalPrices.totals.flightBundleMemberPrice ?? 0) > 0 && luxPlusLPPEnabled}
              fallbackPrice={<PriceRowPrice
                size="M"
                price={totalPrices.totals.flightBundlePrice}
                saleUnit={saleUnit}
              />}
            >
              <LuxPlusPriceStack
                size="M"
                price={totalPrices.totals.flightBundlePrice}
                memberPrice={totalPrices.totals.flightBundleMemberPrice}
                saleUnit={offer.displayPricingAsPerPerson ? 'person' : 'total*'}
              />
            </LuxPlusPriceGuard>
            {offer.displayPricingAsPerPerson && <BedbankTwinShare rooms={rooms} />}
            <PriceRowTaxesAndFees />
            {discount > 0 && !isPromoRate && <PriceRowValueDiscountWithCaption
              size="M"
              originalValue={totalPrices.totals.flightBundleValue}
              discountPercentage={discount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE && discountPosition === 'valueRow' ? discount : undefined}
              discountType="estimate"
            />}
            {!isPromoRate && <OfferPriceDetailsRow
              trigger="price-row"
              triggerSize="M"
              offerPage
              offer={offer}
              rooms={rooms}
              propertyFees={bundlePropertyFees}
              taxesAndFees={bundleTaxesAndFees}
              hotelPrice={bundlePrice}
              hotelMemberPrice={bundleMemberPrice}
              showMemberPrice={luxPlusLPPEnabled && bundleMemberPrice > 0}
              flightPrice={flightPrice}
              duration={rate.nights}
            />}
            {displayBundleMargin && <BedbankMarginText
              margin={bundleMargin!}
              marginAud={bundleMarginAud!}
              supplier={bundleSupplier!}
              type="bedbank_exact_accom"
            />}
          </div>
          <LoyaltyPointsEarnWithBreakdownModal
            calculationRequests={pointsEarnCalculationRequests}
            calculationType="estimate"
          />
        </VerticalSpacer>}
        <AtolProtectedTooltip />
      </AccommodationPackageTilePricingBlock>}
    </AccommodationPackageTilePricingBlocks>
  </>
}

type MappedProps = Pick<Props,
  | 'luxPlusLPPEnabled'
  | 'isFlightsSelected'
  | 'checkIn'
  | 'checkOut'
  | 'flightsEnabled'
  | 'flightOrigin'
  | 'offer'
  | 'rooms'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  luxPlusLPPEnabled: getIsLuxPlusLPPEnabled(appState),
  isFlightsSelected: state.flights,
  checkIn: state.checkInDate,
  checkOut: state.checkOutDate,
  flightsEnabled: offerFlightsEnabled(state, appState),
  flightOrigin: state.airport,
  offer: state.bedbankOffer!,
  rooms: state.rooms,
}), BedbankMultiIndividualOptionTilePricing)
